import React from 'react'
import {MdCheck} from 'react-icons/md'
import styled from '@emotion/styled'
import css from '@emotion/css'
import {mq} from '../../../../../../utils/breakpoints'

import {useDeviceLayout} from '../../../../../../context/deviceLayout'
import {hexToRGBStr} from '@daedalus/core/src/utils/string'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import ContentWrapper from '../../../../../designSystem/helpers/ContentWrapper'
import {IsMemberPlusProp} from '../types'
import {ThemeType} from 'types/Theme'
import Text from '../../../../../designSystem/Text'

const TierPerksList = styled.ul(
  ({theme}) => css`
    position: relative;
    list-style: none;
    padding: 0px;
    padding-left: ${theme.layout.spacing.s600}px;
    margin: 0px;
    text-indent: -20px;
  `
)

const TierPerksListItemIcon = styled(MdCheck)(
  ({theme}) => css`
    color: ${theme.colors.privateDealColor};
    transform: translateY(-2px);
    margin-right: ${theme.layout.spacing.s200}px;
    vertical-align: middle;
  `
)

interface ArrowPosition {
  arrowPosition: number
}
interface TierPerksWrapperArrowProps extends ArrowPosition {
  theme: ThemeType
}
const TierPerksWrapperArrow = styled.div(
  ({theme, arrowPosition}: TierPerksWrapperArrowProps) => css`
    position: relative;
    width: calc(100% - 20px);

    &:before {
      top: -5px;
      left: ${arrowPosition}%;
      border: solid 1px transparent;
      content: '';
      height: 18px;
      width: 18px;
      transform: rotate(45deg) translate(-50%, -50%);
      position: absolute;
      pointer-events: none;
      background-color: ${theme.colors.violet.v100};
      border-left-color: ${theme.colors.violet.v200};
      border-top-color: ${theme.colors.violet.v200};
    }
  `
)

const TierPerksWrapper = styled('div')(
  ({theme}) => css`
    padding: ${theme.layout.spacing.s300}px;
    margin-top: ${theme.layout.spacing.s400}px;
    border-top: 1px solid ${theme.colors.violet.v200};
    background-image: linear-gradient(
      to bottom,
      ${theme.colors.violet.v100},
      rgba(${hexToRGBStr(theme.colors.violet.v100)}, 0)
    );

    ${mq.desktopXs(css`
      border: 1px solid ${theme.colors.violet.v200};
      border-radius: 6px;
    `)}
  `
)

const TierPerksListItem = ({children}: {children: React.ReactNode}) => (
  <Text
    marginTopSize="s300"
    marginBottomSize="s100"
    appearance="caption"
    as="li"
  >
    <TierPerksListItemIcon size={16} />
    {children}
  </Text>
)

const TierPerksMember = () => (
  <>
    <TierPerksListItem>
      <FormattedMessageWrapper
        id="tierProgression.memberFirstBullet"
        defaultMessage="Pay less for the same stay with member deals"
      />
    </TierPerksListItem>
    <TierPerksListItem>
      <FormattedMessageWrapper
        id="tierProgression.memberSecondBullet"
        defaultMessage="Access exclusive Plus deals after completing your first stay"
      />
    </TierPerksListItem>
  </>
)

const TierPerksMemberPlus = () => (
  <>
    <TierPerksListItem>
      <FormattedMessageWrapper
        id="tierProgression.perks.specialPlusMemberOnlyDeals"
        defaultMessage="Special Plus Member-only deals"
      />
    </TierPerksListItem>
    <TierPerksListItem>
      <FormattedMessageWrapper
        id="tierProgression.perks.bestRatesAvailableAnywhere"
        defaultMessage="The best rates available anywhere"
      />
    </TierPerksListItem>
    <TierPerksListItem>
      <FormattedMessageWrapper
        id="tierProgression.perks.freeLifetimeAccess"
        defaultMessage="Free lifetime access"
      />
    </TierPerksListItem>
  </>
)

interface TierPerksProps {
  isMemberPlus: IsMemberPlusProp['isMemberPlus']
  arrowPosition: ArrowPosition['arrowPosition']
}

export const TierPerks = ({
  isMemberPlus,
  arrowPosition = 50
}: TierPerksProps) => {
  const {isMobile} = useDeviceLayout()
  const TierPerks = isMemberPlus ? TierPerksMemberPlus : TierPerksMember

  return (
    <ContentWrapper paddingSize={isMobile ? 's000' : 's500'}>
      <TierPerksWrapper>
        <TierPerksWrapperArrow arrowPosition={arrowPosition} />
        <TierPerksList>
          <TierPerks />
        </TierPerksList>
      </TierPerksWrapper>
    </ContentWrapper>
  )
}
